// src/pages/Documents/EditDocuments.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Stack,
  Divider,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

// Tiptap Imports
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";

// Import TiptapEditor component
import TiptapEditor from "../../../components/Common/TiptapEditor";

const ensureUrlProtocol = (url) => {
  if (!url) return "";
  if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
    return "https://" + url;
  }
  return url;
};

export default function EditDocuments({ document, onClose, onUpdate, chatbotId, permission }) {
  const [pageContent, setPageContent] = useState(
    document?.page_content || "<p></p>"
  );
  const [websiteLink, setWebsiteLink] = useState(document?.website_link || "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [toolbarSaveStatus, setToolbarSaveStatus] = useState("save");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Underline,
      Table.configure({ resizable: true }),
      TableCell,
      TableHeader,
      TableRow,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
    ],
    content: pageContent,
    editorProps: {
      attributes: { class: "tiptap-editor-area p-3 form-control" },
    },
  });

  useEffect(() => {
    const initialContent = document?.page_content || "<p></p>";
    const initialLink = document?.website_link || "";

    setPageContent(initialContent);
    setWebsiteLink(initialLink);

    if (editor && editor.isEditable) {
      const currentEditorContent = editor.getHTML();
      if (currentEditorContent !== initialContent) {
        editor.commands.setContent(initialContent, false);
      }
    }
  }, [document, editor]);

  const handleSave = async () => {
    if (!editor) return;

    const trimmedLink = websiteLink.trim();
    if (
      trimmedLink &&
      !/^(https?:\/\/)/i.test(trimmedLink) &&
      trimmedLink !== "#"
    ) {
      setError(
        "Invalid Source Website Link. Must start with http:// or https://, be '#', or be empty."
      );
      return;
    }

    setIsSaving(true);
    setToolbarSaveStatus("saving");
    setError("");
    setSuccess("");

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const currentContentHtml = editor.getHTML();

      if (!document?.id) throw new Error("Document ID is missing.");

      const finalLink = trimmedLink ? trimmedLink : "#";
      const allDataPayload = {
        ...document,
        page_content: currentContentHtml,
        website_link: finalLink,
      };
      delete allDataPayload.user;

      const response = await axiosInstance.post(
        `/edit-private-data`,
        {
          id: document.id,
          page_content: currentContentHtml,
          all_data: allDataPayload,
        },
        { params }
      );

      if (response.status === 200 && response.data.success) {
        setSuccess("Document updated successfully!");
        setToolbarSaveStatus("saved");
        setTimeout(() => {
          onUpdate();
          setToolbarSaveStatus("save");
        }, 1500);
      } else {
        throw new Error(
          response.data.message ||
            `Failed to update document (Status: ${response.status})`
        );
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update the document.";
      setError(message);
      setToolbarSaveStatus("save");
    } finally {
      setIsSaving(false);
    }
  };

  if (!editor) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="textPrimary">
            Edit Document
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {document?.filename}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider />

      {/* Use TiptapEditor component */}
      <Box sx={{ flexShrink: 0 }}>
        <TiptapEditor editor={editor} height="400px" />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Source Website Link (Optional):
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="url"
            value={websiteLink}
            onChange={(e) => setWebsiteLink(e.target.value)}
            placeholder="https://example.com/source-of-document or #"
            disabled={isSaving}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block" }}>
            If this document originated from a specific URL, you can record it
            here. Use '#' or leave blank for none.
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Stack direction="row" spacing={2} sx={{ p: 2 }}>
        {permission.canModify && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={
              isSaving ? (
                <CircularProgress size={20} />
              ) : toolbarSaveStatus === "saved" ? (
                <i className="fa fa-check" />
              ) : (
                <SaveIcon />
              )
            }
            fullWidth
          >
            {toolbarSaveStatus === "saving"
              ? "Saving..."
              : toolbarSaveStatus === "saved"
              ? "Saved!"
              : "Save"}
          </Button>
        )}
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={isSaving}
          fullWidth
        >
          Cancel
        </Button>
      </Stack>

      {!permission?.canModify && (
        <Alert severity="warning" sx={{ m: 2 }}>
          You don't have permission to edit documents.
        </Alert>
      )}
    </Box>
  );
}